import { EditorContent, useEditorState } from "@tiptap/react"
import { useCallback, useState } from "react"
import LinkInputModal from "./LinkInputModal"

export class TiptapControlEnablement {
  constructor(
    {
      isHeading1Enabled,
      isHeading2Enabled,
      isBoldEnabled,
      isItalicEnabled,
      isStrikeEnabled,
      isCodeEnabled,
      isLinkEnabled
    } = {
      isHeading1Enabled: true,
      isHeading2Enabled: true,
      isBoldEnabled: true,
      isItalicEnabled: true,
      isStrikeEnabled: true,
      isCodeEnabled: true,
      isLinkEnabled: true
    }
  ) {
    this.isHeading1Enabled = isHeading1Enabled
    this.isHeading2Enabled = isHeading2Enabled
    this.isBoldEnabled = isBoldEnabled
    this.isItalicEnabled = isItalicEnabled
    this.isStrikeEnabled = isStrikeEnabled
    this.isCodeEnabled = isCodeEnabled
    this.isLinkEnabled = isLinkEnabled
  }
}

export default function Tiptap({
  editor,
  controlEnablement = new TiptapControlEnablement()
}) {
      return (
        <div className="flex flex-col h-full">
          <MenuBar editor={editor} controlEnablement={controlEnablement} />
          <div className="rounded-md border border-base-300">
            <EditorContent editor={editor} />
          </div>
        </div>
      )
}

function MenuBar({ editor, controlEnablement }) {

    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
    function onLinkModalDismiss() {
      setIsLinkModalOpen(false)
    }

    function onLinkModalSave(url) {
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run()
      } else {
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
      }
      onLinkModalDismiss()
    }

    // Read the current editor's state, and re-render the component when it changes
    const editorState = useEditorState({
      editor,
      selector: ctx => {
        return {
          isBold: ctx.editor.isActive('bold') ?? false,
          canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
          isItalic: ctx.editor.isActive('italic') ?? false,
          canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
          isStrike: ctx.editor.isActive('strike') ?? false,
          canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
          isCode: ctx.editor.isActive('code') ?? false,
          canCode: ctx.editor.can().chain().toggleCode().run() ?? false,
          canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
          isParagraph: ctx.editor.isActive('paragraph') ?? false,
          isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
          isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
          isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
          isHeading4: ctx.editor.isActive('heading', { level: 4 }) ?? false,
          isHeading5: ctx.editor.isActive('heading', { level: 5 }) ?? false,
          isHeading6: ctx.editor.isActive('heading', { level: 6 }) ?? false,
          isBulletList: ctx.editor.isActive('bulletList') ?? false,
          isOrderedList: ctx.editor.isActive('orderedList') ?? false,
          isCodeBlock: ctx.editor.isActive('codeBlock') ?? false,
          isBlockquote: ctx.editor.isActive('blockquote') ?? false,
          canUndo: ctx.editor.can().chain().undo().run() ?? false,
          canRedo: ctx.editor.can().chain().redo().run() ?? false,
          isLink: ctx.editor.isActive('link') ?? false
        }
      },
    })

    const setLink = useCallback(() => {
      setIsLinkModalOpen(true)
    }, [editor])
  
    return (
      <div className="control-group">
        <div className="button-group flex flex-row gap-1 py-4">
        {
          controlEnablement.isHeading1Enabled && (
            <button
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              // disabled={!editorState.canHeading1}
              className={`btn btn-sm btn-soft ${editorState.isHeading1 ? 'btn-primary' : 'btn-neutral'}`}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g>
              <path className="stroke-base-content" d="M16 10L19 9L19 19M3 5V12M3 12V19M3 12H11M11 5V12M11 12V19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              </svg>
            </button>
          )
        }
        {
          controlEnablement.isHeading2Enabled && (
            <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            // disabled={!editorState.canHeading1}
            className={`btn btn-sm btn-soft ${editorState.isHeading2 ? 'btn-primary' : 'btn-neutral'}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g>
            <path className="stroke-base-content" d="M15 12.5V12C15 10.3431 16.3431 9 18 9H18.1716C19.7337 9 20.9996 10.2665 20.9996 11.8286C20.9996 12.5788 20.702 13.2982 20.1716 13.8286L15 19.0002L21 19M3 5V12M3 12V19M3 12H11M11 5V12M11 12V19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
            </svg>
          </button>
          )
        }
        {
          controlEnablement.isBoldEnabled && (
            <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editorState.canBold}
            className={`btn btn-sm btn-soft ${editorState.isBold ? 'btn-primary' : 'btn-neutral'}`}
          >
            <svg className="w-4 h-4 fill-base-content" viewBox="0 0 16 16" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M2 1H8.625C11.0412 1 13 2.95875 13 5.375C13 6.08661 12.8301 6.75853 12.5287 7.35243C13.4313 8.15386 14 9.32301 14 10.625C14 13.0412 12.0412 15 9.625 15H2V1ZM5.5 9.75V11.5H9.625C10.1082 11.5 10.5 11.1082 10.5 10.625C10.5 10.1418 10.1082 9.75 9.625 9.75H5.5ZM5.5 6.25H8.625C9.10825 6.25 9.5 5.85825 9.5 5.375C9.5 4.89175 9.10825 4.5 8.625 4.5H5.5V6.25Z"/>
            </svg>
          </button>
          )
        }
        {
          controlEnablement.isItalicEnabled && (
            <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editorState.canItalic}
            className={`btn btn-sm btn-soft ${editorState.isItalic ? 'btn-primary' : 'btn-neutral'}`}
          >
            <svg className="w-4 h-4 fill-base-content" viewBox="0 0 16 16">
            <path d="M14 1H5V4H7.75219L5.08553 12H2V15H11V12H8.24781L10.9145 4H14V1Z"/>
            </svg>
          </button>
          )
        }
        {
          controlEnablement.isStrikeEnabled && (
            <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editorState.canStrike}
            className={`btn btn-sm btn-soft ${editorState.isStrike ? 'btn-primary' : 'btn-neutral'}`}
          >
            <svg className="w-4 h-4 fill-base-content" viewBox="0 0 1920 1920">
                <path d="M1471.2 1261.689c9.24 67.2 4.92 138.84-16.32 215.64-53.16 190.08-176.64 319.56-348 364.8-46.44 12.24-94.56 17.76-143.04 17.76-209.16 0-424.92-104.04-546.84-225.12l-52.44-56.04 175.68-163.68 49.2 52.92c98.76 97.92 303.48 182.16 456.24 142.08 89.28-23.64 147.48-87.96 177.96-196.92 16.56-60 17.16-109.44 3.12-151.44Zm-31.92-991.08-163.8 175.32c-105.12-98.16-319.2-176.16-469.8-134.76-85.8 23.28-141.6 82.08-170.64 179.76-54.48 183.24 66.72 252 377.76 345.48 71.04 21.36 133.56 40.68 183.96 65.28H1920v240H0v-240h561.72c-135.6-96.84-226.68-243.6-156.72-479.16 67.08-225.84 220.68-311.16 337.8-343.08 247.8-66.72 543.6 48.36 696.48 191.16Z" fill-rule="evenodd"/>
            </svg>
          </button>
          )
        }
        {
          controlEnablement.isCodeEnabled && (
            <button
            onClick={() => editor.chain().focus().toggleCode().run()}
            disabled={!editorState.canCode}
            className={`btn btn-sm btn-soft ${editorState.isCode ? 'btn-primary' : 'btn-neutral'}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 16 16">
            <path className="fill-base-content" d="M8.01005 0.858582L6.01005 14.8586L7.98995 15.1414L9.98995 1.14142L8.01005 0.858582Z"/>
            <path className="fill-base-content" d="M12.5 11.5L11.0858 10.0858L13.1716 8L11.0858 5.91422L12.5 4.5L16 8L12.5 11.5Z"/>
            <path className="fill-base-content" d="M2.82843 8L4.91421 10.0858L3.5 11.5L0 8L3.5 4.5L4.91421 5.91422L2.82843 8Z"/>
            </svg>
          </button>
          )
        }
        {
          controlEnablement.isLinkEnabled && (
            <button
            onClick={() => setLink()}
            disabled={editorState.isLink}
            className={`btn btn-sm btn-soft ${editorState.isLink ? 'btn-primary' : 'btn-neutral'}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path className="stroke-base-content" d="M14 7H16C18.7614 7 21 9.23858 21 12C21 14.7614 18.7614 17 16 17H14M10 7H8C5.23858 7 3 9.23858 3 12C3 14.7614 5.23858 17 8 17H10M8 12H16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          )
        }
        {
          controlEnablement.isLinkEnabled && (
            <button
            onClick={() => editor.chain().focus().unsetLink().run()}
            disabled={!editorState.isLink}
            className={`btn btn-sm btn-soft ${!editorState.isLink ? 'btn-primary' : 'btn-neutral'}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path className="fill-base-content" d="M14 6C13.4477 6 13 6.44772 13 7C13 7.55228 13.4477 8 14 8V6ZM10 18C10.5523 18 11 17.5523 11 17C11 16.4477 10.5523 16 10 16V18ZM14 16C13.4477 16 13 16.4477 13 17C13 17.5523 13.4477 18 14 18V16ZM8 11C7.44772 11 7 11.4477 7 12C7 12.5523 7.44772 13 8 13V11ZM3.70711 2.29289C3.31658 1.90237 2.68342 1.90237 2.29289 2.29289C1.90237 2.68342 1.90237 3.31658 2.29289 3.70711L3.70711 2.29289ZM20.2929 21.7071C20.6834 22.0976 21.3166 22.0976 21.7071 21.7071C22.0976 21.3166 22.0976 20.6834 21.7071 20.2929L20.2929 21.7071ZM19.4115 14.0898C19.1224 14.5604 19.2696 15.1763 19.7402 15.4653C20.2108 15.7544 20.8266 15.6072 21.1157 15.1366L19.4115 14.0898ZM12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11V13ZM7.08376 7.08376L7.79087 6.37666L7.08376 7.08376ZM8 16C5.79086 16 4 14.2091 4 12H2C2 15.3137 4.68629 18 8 18V16ZM16 8C18.2091 8 20 9.79086 20 12H22C22 8.68629 19.3137 6 16 6V8ZM14 8H16V6H14V8ZM10 16H8V18H10V16ZM16 16H14V18H16V16ZM20 12C20 12.7678 19.7846 13.4823 19.4115 14.0898L21.1157 15.1366C21.6767 14.2232 22 13.1478 22 12H20ZM8 13H12V11H8V13ZM2.29289 3.70711L6.37666 7.79087L7.79087 6.37666L3.70711 2.29289L2.29289 3.70711ZM6.90166 6.10048C4.11245 6.61703 2 9.06076 2 12H4C4 10.0423 5.40739 8.41122 7.26586 8.06704L6.90166 6.10048ZM6.37666 7.79087L16.2091 17.6233L17.6233 16.2091L7.79087 6.37666L6.37666 7.79087ZM16.2091 17.6233L20.2929 21.7071L21.7071 20.2929L17.6233 16.2091L16.2091 17.6233ZM16.7341 15.933C16.4969 15.9769 16.2516 16 16 16V18C16.3743 18 16.7415 17.9656 17.0983 17.8995L16.7341 15.933Z"/>
            </svg>
          </button>
          )
        }
        </div>
        {
          isLinkModalOpen && <LinkInputModal isLinkInputModalOpen={isLinkModalOpen} onLinkInputSaveClick={onLinkModalSave} onLinkInputModalDismiss={onLinkModalDismiss} initialLink={editor.getAttributes('link').href} />
        }
      </div>
    )
  }
